import {
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from "react";

type InlineActionTone = "accent" | "success" | "warning" | "danger" | "neutral";
type InlineActionEmphasis = "solid" | "outline" | "soft";

const TONE_COLORS: Record<InlineActionTone, string> = {
  accent: "var(--login-accent)",
  success: "#0f766e",
  warning: "#d97706",
  danger: "var(--login-error, #b42318)",
  neutral: "var(--login-muted)",
};

function buildButtonStyle(
  tone: InlineActionTone,
  emphasis: InlineActionEmphasis,
  disabled: boolean,
  hovered: boolean,
): CSSProperties {
  const color = TONE_COLORS[tone];
  const isSolid = emphasis === "solid";

  return {
    minHeight: "2rem",
    padding: "0.35rem 0.58rem",
    borderRadius: "0.42rem",
    border: `1px solid ${isSolid ? color : `color-mix(in srgb, ${color} 72%, var(--login-border))`}`,
    background: isSolid
      ? color
      : emphasis === "soft"
        ? `color-mix(in srgb, ${color} ${hovered ? 14 : 8}%, transparent)`
        : hovered
          ? `color-mix(in srgb, ${color} 8%, var(--login-surface))`
          : "var(--login-surface)",
    color: isSolid ? "var(--login-surface)" : color,
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700,
    fontSize: "0.8rem",
    lineHeight: 1,
    letterSpacing: "0.05em",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.58 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.3rem",
    transform: hovered && !disabled ? "translateY(-1px)" : "translateY(0)",
    transition:
      "transform 0.16s ease, opacity 0.16s ease, background 0.16s ease, box-shadow 0.16s ease",
    boxShadow:
      hovered && !disabled
        ? `0 8px 18px color-mix(in srgb, ${color} 18%, transparent)`
        : "none",
  };
}

export function InlineActionButton({
  tone = "accent",
  emphasis = "outline",
  icon,
  style,
  disabled = false,
  children,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: InlineActionTone;
  emphasis?: InlineActionEmphasis;
  icon?: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      data-tone={tone}
      data-emphasis={emphasis}
      disabled={disabled}
      {...buttonProps}
      style={{
        ...buildButtonStyle(tone, emphasis, disabled, hovered),
        ...style,
      }}
      onMouseEnter={(event) => {
        setHovered(true);
        buttonProps.onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setHovered(false);
        buttonProps.onMouseLeave?.(event);
      }}
    >
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </button>
  );
}
